import React, { useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
// @ts-ignore - embla-carousel-autoplay types
import Autoplay from 'embla-carousel-autoplay';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRight, Pause, Play } from 'lucide-react';
// import { HoverLift, Floating, PulseGlow, GradientShift } from '@/components/ui/motion-primitives';

interface IntegrationSlide {
  id: string;
  integrationId: string;
  landingPath?: string;
  placeholderImage: string;
}

interface IntegrationCarouselProps {
  className?: string;
  enableAnimations?: boolean;
  autoplay?: boolean;
  autoplayDelay?: number;
}

const IntegrationCarousel: React.FC<IntegrationCarouselProps> = ({
  className = '',
  enableAnimations = true,
  autoplay = true,
  autoplayDelay = 5000,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = React.useState(autoplay);
  
  // Create autoplay plugin with configuration
  const autoplayPlugin = React.useRef(
    Autoplay({ 
      delay: autoplayDelay, 
      stopOnInteraction: false,
      stopOnMouseEnter: true,
    })
  );

  // Toggle play/pause
  const toggleAutoplay = useCallback(() => {
    const autoplayInstance = autoplayPlugin.current;
    if (!autoplayInstance) return;

    if (isPlaying) {
      autoplayInstance.stop();
      setIsPlaying(false);
    } else {
      autoplayInstance.play();
      setIsPlaying(true);
    }
  }, [isPlaying]);

  // Define integration slides with their data
  // Note: Translation keys are in homepage.json under integrations.carousel.slides.{id}
  const slides: IntegrationSlide[] = [
    {
      id: 'claude',
      integrationId: 'claude_desktop',
      landingPath: '/integrations/claude-desktop',
      placeholderImage: 'https://placehold.co/800x500/8B5CF6/FFFFFF/png?text=Claude+Desktop+Memory',
    },
    {
      id: 'cursor',
      integrationId: 'cursor',
      landingPath: '/integrations/cursor',
      placeholderImage: 'https://placehold.co/800x500/111827/FFFFFF/png?text=Cursor+Memory',
    },
    {
      id: 'customgpt',
      integrationId: 'custom_gpt_action',
      landingPath: '/integrations/chatgpt-custom-actions',
      placeholderImage: '/images/integrations/customgpt-demo.png',
    },
    {
      id: 'chrome',
      integrationId: 'chrome_extension',
      landingPath: '/integrations/chrome-extension',
      placeholderImage: '/images/integrations/extension-demo.png',
    },
    {
      id: 'zapier',
      integrationId: 'zapier',
      landingPath: '/integrations/zapier',
      placeholderImage: '/images/integrations/zapier-demo.png',
    },
    {
      id: 'n8n',
      integrationId: 'n8n',
      landingPath: '/integrations/n8n',
      placeholderImage: 'https://placehold.co/800x500/EA4B71/FFFFFF/png?text=n8n+Memory+Integration',
    },
  ];

  // Helper function to get translation with static keys for validation
  const getSlideTranslation = (slideId: string, key: 'title' | 'description' | 'cta'): string => {
    // Static mapping to satisfy i18n validation
    const translationMap: Record<string, Record<string, string>> = {
      customgpt: {
        title: t('integrations.carousel.slides.customgpt.title', { ns: 'homepage' }),
        description: t('integrations.carousel.slides.customgpt.description', { ns: 'homepage' }),
        cta: t('integrations.carousel.slides.customgpt.cta', { ns: 'homepage' }),
      },
      chrome: {
        title: t('integrations.carousel.slides.chrome.title', { ns: 'homepage' }),
        description: t('integrations.carousel.slides.chrome.description', { ns: 'homepage' }),
        cta: t('integrations.carousel.slides.chrome.cta', { ns: 'homepage' }),
      },
      zapier: {
        title: t('integrations.carousel.slides.zapier.title', { ns: 'homepage' }),
        description: t('integrations.carousel.slides.zapier.description', { ns: 'homepage' }),
        cta: t('integrations.carousel.slides.zapier.cta', { ns: 'homepage' }),
      },
      n8n: {
        title: t('integrations.carousel.slides.n8n.title', { ns: 'homepage' }),
        description: t('integrations.carousel.slides.n8n.description', { ns: 'homepage' }),
        cta: t('integrations.carousel.slides.n8n.cta', { ns: 'homepage' }),
      },
      claude: {
        title: t('integrations.carousel.slides.claude.title', { ns: 'homepage' }),
        description: t('integrations.carousel.slides.claude.description', { ns: 'homepage' }),
        cta: t('integrations.carousel.slides.claude.cta', { ns: 'homepage' }),
      },
      cursor: {
        title: t('integrations.carousel.slides.cursor.title', { ns: 'homepage' }),
        description: t('integrations.carousel.slides.cursor.description', { ns: 'homepage' }),
        cta: t('integrations.carousel.slides.cursor.cta', { ns: 'homepage' }),
      },
    };

    return translationMap[slideId]?.[key] || '';
  };

  const handleSlideClick = (slide: IntegrationSlide) => {
    if (slide.landingPath) {
      navigate(slide.landingPath);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.4, 0, 0.2, 1] as const, // easeOut curve
      },
    },
  };

  return (
    <motion.div
      className={`w-full ${className}`}
      variants={enableAnimations ? containerVariants : undefined}
      initial={enableAnimations ? 'hidden' : undefined}
      animate={enableAnimations ? 'visible' : undefined}
    >
      {/* Section Header */}
      <motion.div
        className="text-center mb-16 space-y-6"
        initial={enableAnimations ? { opacity: 0, y: 30 } : undefined}
        animate={enableAnimations ? { opacity: 1, y: 0 } : undefined}
        transition={enableAnimations ? { duration: 0.7, delay: 0.2 } : undefined}
      >
        <motion.h2
          className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight"
          initial={enableAnimations ? { opacity: 0, scale: 0.9 } : undefined}
          animate={enableAnimations ? { opacity: 1, scale: 1 } : undefined}
          transition={enableAnimations ? { duration: 0.8, delay: 0.3 } : undefined}
        >
          <span className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 bg-clip-text text-transparent">
            {t('integrations.carousel.title', { ns: 'homepage' })}
          </span>
        </motion.h2>
        <motion.p
          className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed font-light"
          initial={enableAnimations ? { opacity: 0, y: 20 } : undefined}
          animate={enableAnimations ? { opacity: 1, y: 0 } : undefined}
          transition={enableAnimations ? { duration: 0.6, delay: 0.5 } : undefined}
        >
          {t('integrations.carousel.subtitle', { ns: 'homepage' })}
        </motion.p>
      </motion.div>

      {/* Carousel */}
      <motion.div
        className="relative px-4 md:px-12"
        initial={enableAnimations ? { opacity: 0 } : undefined}
        animate={enableAnimations ? { opacity: 1 } : undefined}
        transition={enableAnimations ? { duration: 0.6, delay: 0.6 } : undefined}
      >
        <Carousel
          opts={{
            align: 'center',
            loop: true,
          }}
          plugins={autoplay ? [autoplayPlugin.current] : []}
          className="w-full"
        >
          <CarouselContent className="-ml-2 md:-ml-4">
            {slides.map((slide) => (
              <CarouselItem key={slide.id} className="pl-2 md:pl-4">
                <Card className="border-2 border-slate-200 dark:border-slate-700 hover:border-purple-400 dark:hover:border-purple-600 transition-all duration-300 overflow-hidden">
                  <CardContent className="p-0">
                    {/* Image Container */}
                    <div className="relative aspect-video bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900">
                      <img
                        src={slide.placeholderImage}
                        alt={getSlideTranslation(slide.id, 'title')}
                        className="w-full h-full object-cover"
                      />
                      {/* Overlay gradient for better text readability */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

                      {/* Text Content Overlay */}
                      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
                        <h3 className="text-2xl md:text-3xl font-bold mb-2">
                          {getSlideTranslation(slide.id, 'title')}
                        </h3>
                        <p className="text-sm md:text-base text-white/90 mb-4 line-clamp-2">
                          {getSlideTranslation(slide.id, 'description')}
                        </p>
                        <Button
                          variant="sanctuary"
                          size="sm"
                          onClick={() => handleSlideClick(slide)}
                          className="group"
                        >
                          {getSlideTranslation(slide.id, 'cta')}
                          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
          
          {/* Navigation Buttons */}
          <CarouselPrevious className="hidden md:flex -left-6 bg-white dark:bg-slate-800 border-2 border-purple-300 dark:border-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/20" />
          <CarouselNext className="hidden md:flex -right-6 bg-white dark:bg-slate-800 border-2 border-purple-300 dark:border-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/20" />
        </Carousel>

        {/* Play/Pause Control */}
        {autoplay && (
          <div className="flex justify-center mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleAutoplay}
              className="bg-white dark:bg-slate-800 border-purple-300 dark:border-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/20"
            >
              {isPlaying ? (
                <>
                  <Pause className="w-4 h-4 mr-2" />
                  Pause
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Play
                </>
              )}
            </Button>
          </div>
        )}
      </motion.div>

      {/* View All CTA */}
      <div className="text-center mt-8">
        <Button
          variant="outline"
          size="lg"
          onClick={() => navigate('/integrations')}
          className="group"
        >
          {t('integrations.viewAllIntegrations', { ns: 'homepage' })}
          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
        </Button>
      </div>
    </motion.div>
  );
};

export default IntegrationCarousel;

